import { api } from "@/lib/api";
import type {
  AddAllResponse,
  AddMovieRequest,
  CollectionDetail,
  CollectionListEntry,
  GrabRecord,
  Movie,
  MovieCollection,
  MovieLookupResult,
  UpdateMovieRequest,
} from "./types";

/** Compact movie detail returned by `GET /api/v1/movies/lookup/:tmdb_id`,
 *  used by the Add-Movie modal so it can decide whether to surface the
 *  "Monitor entire collection" checkbox. Issue #49. */
export interface MovieLookupDetail {
  tmdb_id: number;
  title: string;
  year: number | null;
  overview: string | null;
  poster_url: string | null;
  belongs_to_collection: {
    tmdb_id: number;
    name: string;
    poster_url: string | null;
  } | null;
}

export interface MoviesScanStatus {
  running: boolean;
  last_scan_at: string | null;
  unmatched_count: number;
}

export interface MoviesUnmatchedFile {
  id: number;
  path: string;
  reason: string;
  scanned_at: string;
}

export interface MoviesSettingsResponse {
  library_path: string | null;
  library_path_source: "db" | "env" | null;
  scan_interval_hours: number;
  last_scan_at: string | null;
  refresh_interval_hours: number;
  last_refresh_at: string | null;
  /** Global minimum-availability gate (issue #46). One of `announced` |
   *  `in-cinemas` | `released` | `physical`. Per-movie override lives on
   *  the movies row. */
  minimum_availability:
    | "announced"
    | "in-cinemas"
    | "released"
    | "physical";
  /** Two-letter ISO 3166-1 region code TMDB release-dates lookups filter
   *  against. Default `"US"`. */
  tmdb_region: string;
  /** BCP-47 tag (e.g. `en-US`, `fr-FR`) threaded through every TMDB call.
   *  Drives which language gets baked into the main `movies` row's
   *  title / overview. Issue #53. */
  metadata_language: string;
  /** Additional BCP-47 tags fanned out into the
   *  `movie_metadata_translations` side-table on every add + refresh.
   *  Empty array = single-language mode. Issue #53. */
  alt_metadata_languages: string[];
  /** Migration 0062: alternate root the importer hardlinks to when a
   *  movie is flagged `private`. NULL/blank disables routing — private
   *  becomes UI-visibility only and the household media server still
   *  sees the file at the public path. */
  private_library_path: string | null;
  /** Migration 0062: base URL of the private Jellyfin instance the UI
   *  Watch button deep-links into. NULL/blank hides the button. */
  private_jellyfin_url: string | null;
  /** Migration 0062 + private-promoter: in-container path the
   *  background promoter task scans for orphan manual-search private
   *  downloads. Resolved value (default applied), so the UI always has
   *  something to seed the field with. */
  private_promoter_source_dir: string;
}

/** One persisted translation row for a movie, returned by
 *  `GET /api/v1/movies/:id/translations`. Issue #53. */
export interface MovieTranslation {
  language: string;
  title: string | null;
  overview: string | null;
  tagline: string | null;
  fetched_at: string;
}

/** Migration 0062: tri-state filter over `movies.private`.
 *
 *   - "public" (default) → only non-private rows
 *   - "private"          → only private rows (the dedicated tab)
 *   - "all"              → both
 */
export type MoviePrivacy = "public" | "private" | "all";

function privacyParam(p: MoviePrivacy): string {
  switch (p) {
    case "private":
      return "?private=true";
    case "all":
      return "?private=all";
    default:
      // Backend defaults to public; sending no param keeps the URL
      // clean for the common case.
      return "";
  }
}

export const moviesApi = {
  list: (privacy: MoviePrivacy = "public") =>
    api<Movie[]>(`/api/v1/movies${privacyParam(privacy)}`),
  get: (id: number) => api<Movie>(`/api/v1/movies/${id}`),
  lookup: (q: string) =>
    api<MovieLookupResult[]>(`/api/v1/movies/lookup?q=${encodeURIComponent(q)}`),
  lookupDetail: (tmdbId: number) =>
    api<MovieLookupDetail>(`/api/v1/movies/lookup/${tmdbId}`),
  add: (body: AddMovieRequest) =>
    api<Movie>("/api/v1/movies", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: UpdateMovieRequest) =>
    api<Movie>(`/api/v1/movies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/movies/${id}`, { method: "DELETE" }),
  search: (id: number) =>
    api<GrabRecord>(`/api/v1/movies/${id}/search`, { method: "POST" }),
  /** GET /api/v1/movies/:id/translations — alt-language metadata rows
   *  populated by the configured fan-out. Issue #53. */
  translations: (id: number) =>
    api<MovieTranslation[]>(`/api/v1/movies/${id}/translations`),

  /** Monitored movies without a file in the library. */
  wanted: (limit: number = 100, privacy: MoviePrivacy = "public") => {
    // Same tri-state as `list`. Default keeps the historical "public
    // only" semantics so the household-facing Wanted tab doesn't leak
    // private titles.
    const priv =
      privacy === "private"
        ? "&private=true"
        : privacy === "all"
          ? "&private=all"
          : "";
    return api<Movie[]>(`/api/v1/movies/wanted?limit=${limit}${priv}`);
  },

  scan: {
    start: () =>
      api<{ ok: boolean }>("/api/v1/movies/scan", { method: "POST" }),
    status: () => api<MoviesScanStatus>("/api/v1/movies/scan/status"),
    unmatched: (limit: number = 200) =>
      api<MoviesUnmatchedFile[]>(
        `/api/v1/movies/scan/unmatched?limit=${limit}`,
      ),
    claim: (unmatchedId: number, body: { movie_id: number }) =>
      api<void>(`/api/v1/movies/scan/unmatched/${unmatchedId}/claim`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  collections: {
    list: () =>
      api<CollectionListEntry[]>("/api/v1/collections"),
    get: (tmdbId: number) =>
      api<CollectionDetail>(`/api/v1/collections/${tmdbId}`),
    monitor: (tmdbId: number, monitored: boolean) =>
      api<MovieCollection>(`/api/v1/collections/${tmdbId}/monitor`, {
        method: "POST",
        body: JSON.stringify({ monitored }),
      }),
    addAll: (tmdbId: number) =>
      api<AddAllResponse>(`/api/v1/collections/${tmdbId}/add-all`, {
        method: "POST",
      }),
  },

  settings: {
    get: () => api<MoviesSettingsResponse>("/api/v1/settings/movies"),
    update: (body: {
      library_path?: string;
      scan_interval_hours?: number;
      refresh_interval_hours?: number;
      minimum_availability?:
        | "announced"
        | "in-cinemas"
        | "released"
        | "physical";
      tmdb_region?: string;
      /** BCP-47 tag. Empty string reverts to default. Issue #53. */
      metadata_language?: string;
      /** Replacement alt-language list. Empty array clears the setting.
       *  Issue #53. */
      alt_metadata_languages?: string[];
      /** Migration 0062: alternate root for private movies. Empty
       *  string clears (private routing disabled). */
      private_library_path?: string;
      /** Migration 0062: base URL of the private Jellyfin instance.
       *  Empty string clears (Watch button hidden). */
      private_jellyfin_url?: string;
      /** Migration 0062 + private-promoter: override the in-container
       *  source dir the promoter scans. Empty string reverts to the
       *  default (`/downloads/completed/private`). */
      private_promoter_source_dir?: string;
    }) =>
      api<MoviesSettingsResponse>("/api/v1/settings/movies", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
};
